import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useContextStateManagement } from "../context/ContextApi";
import { SchemaFieldEditor } from "./SchemaFieldEditor";

// Container for editing a practice's LeadGenerationUploadLead schema (the practice-specific
// fields that live in CallerInformation — chart_id/appt_time for a clinic, guest_name/room_number
// for a hotel, etc.). Modeled on client/src/apiFunctions/FunctionModal.js's
// useForm + useFieldArray container pattern.
//
// Deliberately NOT a plain "Save" button — publishing is append-only (see
// server/src/utils/leadSchemaValidation.js), so every publish creates a brand-new
// PracticeSchemaVersion row and deactivates the previous one. This never touches any
// already-written LeadGenerationUploadLead row; it only changes what NEW rows get validated
// against going forward.
const toRequiredSelectValue = (value) => (value === true ? "true" : value === false ? "false" : "");
const fromRequiredSelectValue = (value) => value === "true";

const buildDiff = (originalFields, nextFields) => {
  const originalByName = new Map(originalFields.map((f) => [f.name, f]));
  const nextByName = new Map(nextFields.map((f) => [f.name, f]));

  const added = nextFields.filter((f) => !originalByName.has(f.name));
  const removed = originalFields.filter((f) => !nextByName.has(f.name));
  const changed = nextFields.filter((f) => {
    const prior = originalByName.get(f.name);
    if (!prior) return false;
    return (
      prior.type !== f.type ||
      prior.required !== f.required ||
      prior.pii_sensitivity !== f.pii_sensitivity
    );
  });

  return { added, removed, changed };
};

export const PracticeSchemaEditor = ({ onPublish } = {}) => {
  const { PracticeId, token, usersPostRequest, authorizedGetRequest } =
    useContextStateManagement();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { fields: [] } });

  const { fields, append, remove } = useFieldArray({ control, name: "fields" });

  const [originalFields, setOriginalFields] = useState([]);
  const [schemaVersion, setSchemaVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingDiff, setPendingDiff] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadSchema = async () => {
      if (!PracticeId) return;
      setLoading(true);
      try {
        const response = await authorizedGetRequest(
          token,
          `/api/PracticeSchemaVersion/${PracticeId}`,
        );
        if (cancelled) return;

        const currentFields = response?.schema?.Fields || [];
        setOriginalFields(currentFields);
        setSchemaVersion(response?.schema?.SchemaVersion ?? null);
        reset({
          fields: currentFields.map((f) => ({
            name: f.name,
            type: f.type,
            required: toRequiredSelectValue(f.required),
            pii_sensitivity: f.pii_sensitivity,
          })),
        });
      } catch (error) {
        if (!cancelled) {
          setSubmitError(error.message || "Failed to load current schema");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSchema();
    return () => {
      cancelled = true;
    };
    // authorizedGetRequest/reset intentionally excluded: ContextApi.js doesn't memoize its
    // functions (no useCallback anywhere in that provider), so including them here would refetch
    // on every unrelated context re-render instead of only when PracticeId/token actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token]);

  const buildNextFields = (data) =>
    (data.fields || []).map((f) => ({
      name: String(f.name || "").trim(),
      type: f.type,
      required: fromRequiredSelectValue(f.required),
      pii_sensitivity: f.pii_sensitivity,
    }));

  // Step 1: user clicks "Save New Schema Version" — show a diff, don't publish yet.
  const onReviewChanges = (data) => {
    setSubmitError("");
    setSuccessMsg("");
    const nextFields = buildNextFields(data);
    setPendingDiff({ nextFields, diff: buildDiff(originalFields, nextFields) });
  };

  // Step 2: user confirms the diff — actually publish the new version.
  const confirmPublish = async () => {
    if (!pendingDiff) return;
    setSubmitError("");
    try {
      const response = await usersPostRequest(token, "/api/PracticeSchemaVersion", {
        practiceId: PracticeId,
        Fields: pendingDiff.nextFields,
        Label: "",
      });
      if (response?.error) {
        setSubmitError(
          Array.isArray(response.details) ? response.details.join("; ") : response.error,
        );
        return;
      }
      setOriginalFields(pendingDiff.nextFields);
      setSchemaVersion(response?.schema?.SchemaVersion ?? schemaVersion);
      setSuccessMsg(`Published schema version ${response?.schema?.SchemaVersion}`);
      if (typeof onPublish === "function") {
        onPublish(response?.schema);
      }
    } catch (error) {
      setSubmitError(error.message || "Failed to publish schema");
    } finally {
      setPendingDiff(null);
    }
  };

  if (loading) {
    return <div>Loading current schema…</div>;
  }

  return (
    <div>
      <h5>
        Outbound Call Schema{" "}
        {schemaVersion !== null && (
          <span className="text-muted fs-6">(current version: {schemaVersion})</span>
        )}
      </h5>
      <p className="text-muted">
        Define the practice-specific fields carried on each outbound lead/call (e.g. appointment
        time for a clinic, room number for a hotel). Common fields (name, phone, call status) are
        already handled and don't need to be defined here.
      </p>

      {submitError && <div className="alert alert-danger">{submitError}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      <form onSubmit={handleSubmit(onReviewChanges)}>
        {fields.map((field, index) => (
          <SchemaFieldEditor
            key={field.id}
            index={index}
            register={register}
            errors={errors}
            removeField={remove}
          />
        ))}

        <div className="d-flex gap-2 mt-3">
          <span
            className="simple-btn d-inline-block"
            onClick={() =>
              append({ name: "", type: "", required: "", pii_sensitivity: "" })
            }
          >
            + Add Field
          </span>
          <button type="submit" className="btn btn-primary">
            Save New Schema Version
          </button>
        </div>
      </form>

      {pendingDiff && (
        <div className="border rounded p-3 mt-3 bg-light">
          <h6>Confirm schema change</h6>
          <p className="text-muted">
            This publishes a new schema version. It does not change any already-stored lead — only
            new leads uploaded/dialed from now on will be validated against it.
          </p>
          {pendingDiff.diff.added.length > 0 && (
            <div>
              <strong>Added:</strong> {pendingDiff.diff.added.map((f) => f.name).join(", ")}
            </div>
          )}
          {pendingDiff.diff.removed.length > 0 && (
            <div>
              <strong>Removed:</strong> {pendingDiff.diff.removed.map((f) => f.name).join(", ")}
            </div>
          )}
          {pendingDiff.diff.changed.length > 0 && (
            <div>
              <strong>Changed:</strong> {pendingDiff.diff.changed.map((f) => f.name).join(", ")}
            </div>
          )}
          {pendingDiff.diff.added.length === 0 &&
            pendingDiff.diff.removed.length === 0 &&
            pendingDiff.diff.changed.length === 0 && <div>No effective changes.</div>}

          <div className="d-flex gap-2 mt-2">
            <button className="btn btn-primary" onClick={confirmPublish}>
              Confirm &amp; Publish
            </button>
            <button className="btn btn-secondary" onClick={() => setPendingDiff(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
